/**
 * Routes de l'application.
 *
 * Routage **par code** plutôt que par fichiers : pas d'artefact généré
 * (`routeTree.gen.ts`) à committer ni à régénérer, donc un build déterministe
 * en CI. Le typage de bout en bout est identique.
 */
import { Outlet, createRootRoute, createRoute, createRouter } from "@tanstack/react-router";

import { AdminLayout } from "@/app/layouts/AdminLayout";
import { PublicLayout } from "@/app/layouts/PublicLayout";
import { ErrorPage } from "@/app/pages/ErrorPage";
import { NotFoundPage } from "@/app/pages/NotFoundPage";
import { LegalNoticePage, PrivacyPolicyPage, TermsPage } from "@/features/legal";
import { AccountPage } from "@/features/account";
import { PlansPage, SubscriptionPage } from "@/features/billing";
import {
  AdminAuditLogPage,
  AdminOutboxPage,
  AdminFeatureFlagsPage,
  AdminReportsPage,
  AdminStatsPage,
  AdminUserDetailPage,
  AdminUsersPage,
} from "@/features/admin";
import { RequirePermission } from "@/features/admin/components/RequirePermission";
import {
  CatalogPage,
  CourseEditorPage,
  CoursePage,
  LessonEditorPage,
  MyCoursesPage,
} from "@/features/catalog";
import {
  ForgotPasswordPage,
  LoginPage,
  RegisterPage,
  RequireAuth,
  ResetPasswordPage,
  VerifyEmailPage,
} from "@/features/auth";
import { HomePage } from "@/features/home";
import { ConversationPage, ConversationsPage } from "@/features/messaging";
import { NotificationPreferencesPage, NotificationsPage } from "@/features/notifications";
import {
  CoursePlayerPage,
  GradeSubmissionPage,
  GradingQueuePage,
  LearnerDashboardPage,
  MyCertificatesPage,
  MyLearningPage,
  StudentFilePage,
  TeacherDashboardPage,
  VerifyCertificatePage,
} from "@/features/learning";

/**
 * La racine ne pose aucun cadre : elle n'est qu'un point de montage.
 *
 * Le chrome vient de deux routes « pathless » sœurs — `publicLayoutRoute` et
 * `adminLayoutRoute` — qui portent chacune leur layout sans ajouter de segment
 * d'URL. Mettre `PublicLayout` sur la racine imbriquerait le back-office dans
 * l'en-tête public : deux barres de navigation superposées, et un
 * administrateur qui ne sait plus dans quel contexte il agit.
 */
const rootRoute = createRootRoute({
  component: Outlet,
  notFoundComponent: NotFoundPage,
  // Sans `errorComponent`, une exception non rattrapée dans un composant
  // démonte tout l'arbre React et laisse une page **blanche** : ni message,
  // ni lien, ni moyen de repartir autrement qu'en retapant une URL. C'est
  // l'un des rares états dont personne ne fait de rapport de bug, parce
  // qu'il n'y a rien à décrire.
  errorComponent: ErrorPage,
});

const publicLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "public",
  component: PublicLayout,
});

const homeRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/",
  component: HomePage,
});

/**
 * `validateSearch` typé à la main plutôt que via un schéma : ces paramètres
 * viennent d'un lien reçu par email ou d'une redirection, donc de l'extérieur.
 * On les traite comme éventuellement absents plutôt que de faire échouer la
 * navigation — la page affiche alors un message clair.
 */
const loginRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/login",
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    ...(typeof search["redirect"] === "string" ? { redirect: search["redirect"] } : {}),
  }),
});

const registerRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/register",
  component: RegisterPage,
});

const verifyEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/verify-email",
  component: VerifyEmailPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    ...(typeof search["token"] === "string" ? { token: search["token"] } : {}),
  }),
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/forgot-password",
  component: ForgotPasswordPage,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/reset-password",
  component: ResetPasswordPage,
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    ...(typeof search["token"] === "string" ? { token: search["token"] } : {}),
  }),
});

/**
 * Catalogue public (docs/11 §6). Sous le layout public, et **sans**
 * `RequireAuth` : un visiteur doit pouvoir parcourir et lire les leçons
 * d'aperçu avant d'avoir un compte. Le filtrage de ce qu'il voit est fait
 * côté serveur, pas par l'absence de route.
 */
const catalogRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/courses",
  component: CatalogPage,
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; domain?: string; level?: string } => ({
    ...(typeof search["q"] === "string" && search["q"] !== "" ? { q: search["q"] } : {}),
    ...(typeof search["domain"] === "string" && search["domain"] !== ""
      ? { domain: search["domain"] }
      : {}),
    ...(typeof search["level"] === "string" && search["level"] !== ""
      ? { level: search["level"] }
      : {}),
  }),
});

/**
 * La fiche est adressée par **slug** et non par identifiant : c'est l'URL
 * qu'on partage, et elle doit rester lisible. L'espace enseignant, lui, passe
 * par l'identifiant — il vient de créer le cours et n'a pas à relire son adresse.
 */
const courseRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/courses/$slug",
  component: CoursePage,
});

/**
 * Les offres (docs/10 §8) — **sans `RequireAuth`**.
 *
 * Une grille tarifaire derrière une authentification ferait renoncer avant
 * l'inscription : c'est la page qu'un visiteur consulte pour décider s'il crée
 * un compte. Le serveur sert la même liste à tout le monde, `is_current` en
 * moins.
 */
const plansRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/billing/plans",
  component: PlansPage,
});

const subscriptionRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/billing/subscription",
  component: () => (
    <RequireAuth>
      <SubscriptionPage />
    </RequireAuth>
  ),
});

const notificationsRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/notifications",
  component: () => (
    <RequireAuth>
      <NotificationsPage />
    </RequireAuth>
  ),
});

const notificationPreferencesRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/account/notifications",
  component: () => (
    <RequireAuth>
      <NotificationPreferencesPage />
    </RequireAuth>
  ),
});

/**
 * La messagerie (docs/13).
 *
 * La liste est déclarée **avant** le fil : une route littérale se déclare avant
 * la route paramétrée qui pourrait l'avaler — `/messages/$conversationId` ne
 * capterait pas `/messages`, mais l'ordre reste celui de la règle, et c'est
 * elle qui a évité un `unread-count` lu comme un UUID côté serveur.
 */
const conversationsRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/messages",
  component: () => (
    <RequireAuth>
      <ConversationsPage />
    </RequireAuth>
  ),
});

const conversationRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/messages/$conversationId",
  component: () => (
    <RequireAuth>
      <ConversationPage />
    </RequireAuth>
  ),
});

const accountRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/account",
  component: () => (
    <RequireAuth>
      <AccountPage />
    </RequireAuth>
  ),
});

/**
 * Back-office (docs/16). Sous son propre layout : la barre latérale et le cadre
 * plus sobre rappellent en permanence qu'on agit sur les comptes d'autrui.
 *
 * `createRoute` sans `path` et avec un `id` : une route « pathless » qui porte
 * le layout sans ajouter de segment d'URL, donc `/admin/users` et non
 * `/admin/admin/users`.
 */
/**
 * Espace enseignant. Sous le layout public — il n'a pas la sobriété
 * délibérée du back-office, où l'on agit sur les comptes d'autrui —, mais
 * derrière `RequireAuth` : ces écrans n'ont aucun sens sans session.
 *
 * Aucune `RequirePermission` : `catalog.course.create` est portée par le rôle
 * `teacher`, et un élève qui suivrait le lien verra une liste vide plutôt
 * qu'un refus. Ce sont les **routes serveur** qui refusent l'écriture, ici
 * comme partout (docs/07 §8).
 */
const teachLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "teach",
  component: PublicLayout,
});

const myCoursesRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/courses",
  component: () => (
    <RequireAuth>
      <MyCoursesPage />
    </RequireAuth>
  ),
});

const courseEditorRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/courses/$courseId",
  component: () => (
    <RequireAuth>
      <CourseEditorPage />
    </RequireAuth>
  ),
});

const lessonEditorRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/courses/$courseId/lessons/$lessonId",
  component: () => (
    <RequireAuth>
      <LessonEditorPage />
    </RequireAuth>
  ),
});

/**
 * Le suivi d'une classe (docs/12 §6) et la correction (§4).
 *
 * Sous le même layout que l'écriture des cours : l'enseignant qui corrige et
 * celui qui rédige sont la même personne dans le même contexte, et les séparer
 * en deux espaces l'obligerait à savoir dans lequel il se trouve.
 *
 * Aucune `RequirePermission` ici non plus. Un élève qui suivrait le lien reçoit
 * un 403 du serveur, affiché par `ErrorState` — et c'est le serveur qui est la
 * garde, jamais le routeur (docs/07 §8).
 */
const teachDashboardRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/dashboard",
  component: () => (
    <RequireAuth>
      <TeacherDashboardPage />
    </RequireAuth>
  ),
});

const gradingQueueRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/grading",
  component: () => (
    <RequireAuth>
      <GradingQueuePage />
    </RequireAuth>
  ),
});

/**
 * Déclarée **après** `/teach/grading` : une route littérale se déclare avant la
 * route paramétrée qui pourrait l'avaler.
 */
const gradeSubmissionRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/grading/$attemptId",
  component: () => (
    <RequireAuth>
      <GradeSubmissionPage />
    </RequireAuth>
  ),
});

const studentFileRoute = createRoute({
  getParentRoute: () => teachLayoutRoute,
  path: "/teach/students/$studentId",
  component: () => (
    <RequireAuth>
      <StudentFilePage />
    </RequireAuth>
  ),
});

/**
 * Espace élève (docs/12). Sous son propre layout « pathless », comme
 * l'enseignant : le player et la liste « mes cours » sont un contexte à part,
 * et les mêmes mots — « mes cours » — désignent deux choses différentes selon
 * qu'on écrit un cours ou qu'on en suit un.
 *
 * Derrière `RequireAuth` : une progression n'a aucun sens sans session. Aucune
 * `RequirePermission` en revanche — `learning.enrollment.create` est portée par
 * le rôle `student`, et c'est le **serveur** qui refuse, ici comme partout
 * (docs/07 §8).
 */
const learnLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "learn",
  component: PublicLayout,
});

const myLearningRoute = createRoute({
  getParentRoute: () => learnLayoutRoute,
  path: "/learn",
  component: () => (
    <RequireAuth>
      <MyLearningPage />
    </RequireAuth>
  ),
});

/**
 * ⚠️ Déclarée **avant** `/learn/$slug`, et ce n'est pas cosmétique : sans cet
 * ordre, la route paramétrée avalerait `/learn/dashboard` et chercherait un
 * cours dont le slug serait « dashboard ». Une route littérale se déclare avant
 * la route paramétrée qui pourrait l'avaler.
 */
const learnerDashboardRoute = createRoute({
  getParentRoute: () => learnLayoutRoute,
  path: "/learn/dashboard",
  component: () => (
    <RequireAuth>
      <LearnerDashboardPage />
    </RequireAuth>
  ),
});

/**
 * « Mes attestations ». Déclarée **avant** `/learn/$slug`, comme le tableau de
 * bord et pour la même raison : sans cet ordre, la route paramétrée avalerait
 * `/learn/certificates` et chercherait un cours au slug « certificates ».
 */
const myCertificatesRoute = createRoute({
  getParentRoute: () => learnLayoutRoute,
  path: "/learn/certificates",
  component: () => (
    <RequireAuth>
      <MyCertificatesPage />
    </RequireAuth>
  ),
});

/**
 * Le player est adressé par **slug**, comme la fiche publique : c'est la même
 * ressource vue par quelqu'un d'inscrit, et l'URL qu'on partage doit rester la
 * même à un préfixe près.
 *
 * Déclarée **après** `/learn` : une route littérale se déclare avant la route
 * paramétrée qui pourrait l'avaler — ici les deux ne se recouvrent pas, `/learn`
 * n'ayant pas de segment à capter, mais l'ordre reste celui de la règle.
 */
const coursePlayerRoute = createRoute({
  getParentRoute: () => learnLayoutRoute,
  path: "/learn/$slug",
  component: () => (
    <RequireAuth>
      <CoursePlayerPage />
    </RequireAuth>
  ),
});

const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "admin",
  component: AdminLayout,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/users",
  component: () => (
    <RequirePermission anyOf={["admin.user.read"]}>
      <AdminUsersPage />
    </RequirePermission>
  ),
  validateSearch: (
    search: Record<string, unknown>,
  ): { q?: string; status?: string; role?: string } => ({
    ...(typeof search["q"] === "string" && search["q"] !== "" ? { q: search["q"] } : {}),
    ...(typeof search["status"] === "string" && search["status"] !== ""
      ? { status: search["status"] }
      : {}),
    ...(typeof search["role"] === "string" && search["role"] !== ""
      ? { role: search["role"] }
      : {}),
  }),
});

const adminUserDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/users/$userId",
  component: () => (
    <RequirePermission anyOf={["admin.user.read"]}>
      <AdminUserDetailPage />
    </RequirePermission>
  ),
});

const adminOutboxRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/outbox",
  component: () => (
    <RequirePermission anyOf={["admin.outbox.read"]}>
      <AdminOutboxPage />
    </RequirePermission>
  ),
});

/**
 * La file de modération (docs/16 §2).
 *
 * Sous `RequirePermission` comme ses voisines : la garde d'écran écarte tôt un
 * opérateur sans droit, mais elle ne protège rien — chaque route de l'API
 * revérifie, et la consultation du contenu exige en plus
 * `messaging.message.moderate`, que `admin.report.handle` n'implique pas.
 */
const adminReportsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/reports",
  component: () => (
    <RequirePermission anyOf={["admin.report.handle"]}>
      <AdminReportsPage />
    </RequirePermission>
  ),
});

/**
 * Les statistiques (docs/16 §8). `admin.stats.read`, portée par le seul rôle
 * `admin` : un modérateur trie des signalements, il ne consulte pas le revenu.
 */
const adminStatsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/stats",
  component: () => (
    <RequirePermission anyOf={["admin.stats.read"]}>
      <AdminStatsPage />
    </RequirePermission>
  ),
});

/**
 * Les feature flags (docs/05 §11). `admin.feature_flag.manage`, distincte
 * d'`admin.settings.manage` : couper une fonctionnalité pour toute la
 * plateforme n'est pas régler un paramètre, et les confondre donnerait le
 * coupe-circuit à qui n'a besoin que des réglages.
 */
const adminFeatureFlagsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/feature-flags",
  component: () => (
    <RequirePermission anyOf={["admin.feature_flag.manage"]}>
      <AdminFeatureFlagsPage />
    </RequirePermission>
  ),
});

const adminAuditLogRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/admin/audit-logs",
  component: () => (
    <RequirePermission anyOf={["admin.audit.read"]}>
      <AdminAuditLogPage />
    </RequirePermission>
  ),
});

/**
 * Les routes des features s'ajoutent ici, phase après phase (docs/23).
 *
 * Exporté pour que les tests reconstruisent un routeur sur un historique en
 * mémoire : le `router` ci-dessous est lié à l'historique du navigateur, donc
 * impossible à positionner sur une URL arbitraire sans toucher à `location`.
 */
/**
 * La vérification d'un certificat — **la seule route de l'application sans
 * `RequireAuth`** en dehors de l'authentification et du catalogue public.
 *
 * C'est délibéré et c'est tout l'intérêt : la personne qui vérifie est celle à
 * qui on a présenté une attestation, et exiger un compte rendrait la
 * vérification inutilisable par ceux à qui elle sert (docs/12 §5).
 *
 * Deux routes pour un écran : avec numéro (le lien imprimé sur le PDF) et sans
 * (quelqu'un qui recopie à la main depuis une feuille). La seconde est déclarée
 * avant la première, la règle habituelle — une route littérale avant la route
 * paramétrée qui pourrait l'avaler.
 */
const verifyCertificateFormRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/certificates/verify",
  component: () => <VerifyCertificatePage />,
});

const verifyCertificateRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/certificates/verify/$serial",
  component: function VerifyCertificateRoute() {
    const { serial } = verifyCertificateRoute.useParams();
    return <VerifyCertificatePage serial={serial} />;
  },
});

/**
 * Les pages légales (docs/20 §10).
 *
 * **Trois routes nommées et non `/legal/$document`** : la seconde forme aurait
 * été plus courte et aurait rendu une page vide — sans même un 404 — pour
 * `/legal/nimportequoi`. Ce sont des documents, pas un paramètre.
 *
 * Sous `publicLayoutRoute` et sans garde : ce sont les pages qu'un visiteur
 * consulte **avant** de créer un compte, et les rendre inaccessibles sans
 * session viderait de son sens le fait de les publier.
 */
const legalNoticeRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/legal/notice",
  component: LegalNoticePage,
});

const privacyRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/legal/privacy",
  component: PrivacyPolicyPage,
});

const termsRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: "/legal/terms",
  component: TermsPage,
});

export const routeTree = rootRoute.addChildren([
  publicLayoutRoute.addChildren([
    homeRoute,
    loginRoute,
    registerRoute,
    verifyEmailRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    accountRoute,
    notificationsRoute,
    notificationPreferencesRoute,
    conversationsRoute,
    conversationRoute,
    catalogRoute,
    courseRoute,
    plansRoute,
    subscriptionRoute,
    verifyCertificateFormRoute,
    verifyCertificateRoute,
    legalNoticeRoute,
    privacyRoute,
    termsRoute,
  ]),
  teachLayoutRoute.addChildren([
    myCoursesRoute,
    courseEditorRoute,
    lessonEditorRoute,
    teachDashboardRoute,
    gradingQueueRoute,
    gradeSubmissionRoute,
    studentFileRoute,
  ]),
  learnLayoutRoute.addChildren([
    myLearningRoute,
    learnerDashboardRoute,
    myCertificatesRoute,
    coursePlayerRoute,
  ]),
  adminLayoutRoute.addChildren([
    adminUsersRoute,
    adminUserDetailRoute,
    adminOutboxRoute,
    adminReportsRoute,
    adminStatsRoute,
    adminFeatureFlagsRoute,
    adminAuditLogRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ErrorPage,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
